<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const inboxName = ref('');
const wahaSession = ref('');
const wahaSessions = ref([]);
const isLoadingSessions = ref(false);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const validations = {
  inboxName: { required },
  wahaSession: { required },
};

const v$ = useVuelidate(validations, { inboxName, wahaSession });

const sessionOptions = computed(() => {
  return wahaSessions.value
    .filter(session => session.status === 'WORKING')
    .map(session => ({
      value: session.full_name,
      label: `${session.name}${session.phone_number ? ` (${session.phone_number})` : ''}`,
      phone_number: session.phone_number,
    }));
});

const selectedSessionPhoneNumber = computed(() => {
  if (!wahaSession.value) return '';
  const session = sessionOptions.value.find(s => s.value === wahaSession.value);
  return session?.phone_number || '';
});

const loadWahaSessions = async () => {
  isLoadingSessions.value = true;
  try {
    await store.dispatch('wahaSessions/get');
    wahaSessions.value = store.getters['wahaSessions/getSessions'];

    // Auto-select first working session if available
    if (sessionOptions.value.length > 0 && !wahaSession.value) {
      wahaSession.value = sessionOptions.value[0].value;
    }
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSIONS_LOAD_ERROR'));
  } finally {
    isLoadingSessions.value = false;
  }
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  // Get phone number from selected session
  const phoneNumber = selectedSessionPhoneNumber.value;

  if (!phoneNumber) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.WAHA.NO_PHONE_NUMBER'));
    return;
  }

  try {
    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber,
        provider: 'waha',
        provider_config: {
          waha_session: wahaSession.value.trim(),
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: whatsappChannel.id,
      },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.WAHA.ERROR_MESSAGE'));
  }
};

onMounted(() => {
  loadWahaSessions();
});
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.wahaSession.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSION.LABEL') }}
        </span>
        <select
          v-model="wahaSession"
          :disabled="isLoadingSessions || sessionOptions.length === 0"
          @blur="v$.wahaSession.$touch"
        >
          <option value="" disabled>
            {{
              isLoadingSessions
                ? $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSION.LOADING')
                : sessionOptions.length === 0
                  ? $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSION.NO_SESSIONS')
                  : $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSION.PLACEHOLDER')
            }}
          </option>
          <option
            v-for="option in sessionOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <span v-if="v$.wahaSession.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSION.ERROR') }}
        </span>
        <span
          v-if="sessionOptions.length === 0 && !isLoadingSessions"
          class="text-xs text-n-orange-11 block mt-2"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SESSION.CREATE_SESSION_HINT') }}
        </span>
      </label>
    </div>
    <div class="w-full">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        :is-loading="uiFlags.isCreating"
      />
    </div>
  </form>
</template>
